import { SeoContentType } from '../seo-brief.schema';
import { SeoRuleKey, SeoScoreGroup } from './types';

/**
 * Seuils et poids du score SEO : UNIQUE source de vérité, versionnée.
 * Tout ajustement (calibration §4 de la spec, retours d'Alexandre sur les
 * poids) se fait ici et incrémente `SEO_SCORE_VERSION`.
 *
 * 2026.09.3 (critique du 2026-09-05) : le score est une GATE (bloquants + plancher)
 * et un rapport de règles, pas une note de qualité : le bench a montré que tous
 * les rédacteurs sérieux sortent entre 83 et 90 et que seuls les bloquants
 * discriminent. Poids rebasculés de la forme vers le fond (ciblage 25 → 10,
 * brief 10 → 25), contradictions avec le prompt levées (connecteurs plafonnés
 * au lieu d'exigés, densité et intertitres tolérants au mot-clé reformulé,
 * longueur SERP en bonus, lisibilité non plafonnée vers le simple).
 *
 * 2026.09.4 (relecture des articles générés du 2026-09-06) : trois signatures du texte
 * automatique que rien ne mesurait : sections toutes de la même forme et paragraphes tous de la
 * même taille (`PARAGRAPH_UNIFORMITY`), transitions mécaniques « Le premier… Le second… »
 * (`ENUMERATIVE_SCAFFOLDING`), introduction qui annonce l'article au lieu d'y entrer
 * (`META_INTRO`). Règles de forme justifiées par la lecture, pas par une corrélation GSC :
 * elles restent des WARN/FAIL ordinaires, jamais bloquantes.
 *
 * 2026.10.1 (retour pro du 14/10) : `CTA_COUNT` ne compte plus l'indicatif (« vous prenez
 * rendez-vous pour votre coupe ») ni un infinitif seul (« le stress de réserver ») : un infinitif
 * n'est un appel à l'action qu'introduit par une invitation (« vous pouvez réserver »).
 *
 * 2026.10.2 (retour d'Alexandre du 14/10) : un article pro n'affiche qu'UNE image (la fiche
 * article lit `pictures[0]`, le corps n'accepte pas `img`) : `IMAGE_PRESENT` attend exactement
 * une image au lieu d'une par 500 mots, qui produisait un avertissement incorrigeable au-delà de
 * 1 000 mots. Longueur : plancher de la cible SERP relevé à 800 mots (`SEO_TARGET_WORDS_BOUNDS`,
 * ex-`WORDS_BOUNDS` du brief), défauts par type alignés dessus sauf NEWS (brèves), et
 * `THIN_CONTENT_WORDS` 300 → 600 pour que le bloquant ait un sens avec la tolérance de 20 %.
 */
export const SEO_SCORE_VERSION = '2026.10.2';

/** Poids nominaux par famille (somme = 100). */
export const SEO_GROUP_WEIGHTS: Record<SeoScoreGroup, number> = {
  [SeoScoreGroup.KEYWORD]: 10,
  [SeoScoreGroup.STRUCTURE]: 15,
  [SeoScoreGroup.LENGTH_META]: 10,
  [SeoScoreGroup.READABILITY]: 10,
  [SeoScoreGroup.LINKS]: 10,
  [SeoScoreGroup.IMAGES]: 5,
  [SeoScoreGroup.STYLE]: 15,
  [SeoScoreGroup.BRIEF]: 25,
};

/**
 * Poids relatif de chaque règle DANS son groupe (défaut 1). Les règles « bonus »
 * ne pénalisent pas : leur FAIL vaut 0 point mais elles sont retirées du
 * dénominateur (cf. `BONUS_RULES`).
 */
export const SEO_RULE_WEIGHTS: Partial<Record<SeoRuleKey, number>> = {
  [SeoRuleKey.KW_IN_TITLE]: 2,
  [SeoRuleKey.KW_DENSITY]: 2,
  [SeoRuleKey.KW_IN_INTRO]: 1.5,
  [SeoRuleKey.SINGLE_H1]: 1.5,
  [SeoRuleKey.H2_RATIO]: 1.5,
  [SeoRuleKey.WORD_COUNT]: 2,
  [SeoRuleKey.READABILITY_FR]: 1.5,
  [SeoRuleKey.INTERNAL_LINKS_COUNT]: 2,
  [SeoRuleKey.AI_PHRASES]: 1.5,
  [SeoRuleKey.EM_DASH]: 1.5,
  [SeoRuleKey.OUTLINE_COVERAGE]: 1.5,
  [SeoRuleKey.FACT_TRACE]: 1.5,
  [SeoRuleKey.REQUIRED_LINKS]: 2,
  [SeoRuleKey.REASONING_LEAK]: 2,
};

/**
 * Règles bonus : un PASS rapporte, un FAIL ne coûte rien. `LENGTH_VS_SERP` y
 * est passée en 2026.09.3 : la longueur était comptée deux fois (avec
 * `WORD_COUNT`) sur une médiane SERP gonflée par les agrégateurs, ce qui
 * punissait les articles concis.
 */
export const BONUS_RULES: ReadonlySet<SeoRuleKey> = new Set([
  SeoRuleKey.FAQ_PRESENT,
  SeoRuleKey.META_TITLE_UNIQUE,
  SeoRuleKey.NOMINAL_STACKS,
  SeoRuleKey.EXTERNAL_SOURCE,
  SeoRuleKey.LENGTH_VS_SERP,
]);

/**
 * Règles bloquantes en mode strict (gate QA backend). Bloquent aussi, par
 * `forceBlocking` : WORD_COUNT sous THIN_CONTENT_WORDS, INTRO_PRESENT sans
 * aucun paragraphe avant le premier intertitre, FACT_TRACE au-delà de
 * FACT_TRACE_BLOCK_UNTRACED chiffres non tracés.
 */
export const BLOCKING_RULES: ReadonlySet<SeoRuleKey> = new Set([
  SeoRuleKey.KW_IN_TITLE,
  SeoRuleKey.KW_STUFFING,
  SeoRuleKey.SINGLE_H1,
  SeoRuleKey.FORBIDDEN_LINK,
  SeoRuleKey.EM_DASH,
  SeoRuleKey.FORBIDDEN_TERMS,
  SeoRuleKey.REASONING_LEAK,
  SeoRuleKey.REQUIRED_LINKS,
]);

/** Score de passage (mode strict) et bornes des grades. */
export const SEO_GATE_MIN_SCORE = 75;
export const SEO_GRADE_BOUNDS = { EXCELLENT: 85, GOOD: 70, NEEDS_WORK: 50 } as const;

/** Longueur cible par défaut (mots) par type de contenu : [min, max]. */
export const DEFAULT_WORD_COUNT_RANGE: Record<SeoContentType, [number, number]> = {
  [SeoContentType.LOCAL_GUIDE]: [800, 1200],
  [SeoContentType.GUIDE]: [1200, 2000],
  [SeoContentType.TUTORIAL]: [1000, 1800],
  [SeoContentType.COMPARISON]: [1200, 2000],
  /** Seule exception sous le plancher : une brève reste courte. */
  [SeoContentType.NEWS]: [400, 800],
  [SeoContentType.FAQ]: [800, 1200],
};
export const FALLBACK_WORD_COUNT_RANGE: [number, number] = [800, 1500];
/**
 * Bornes de la longueur cible issue de la SERP (médiane × 1,1) : le moteur n'en sort jamais, et
 * la cible proposée par le LLM du brief est rejetée hors de ces bornes. Le plancher est à 800 :
 * la médiane SERP, tirée vers le bas par les pages fines, ne doit pas produire un article court.
 */
export const SEO_TARGET_WORDS_BOUNDS: [number, number] = [800, 2500];
/** En dessous : thin content, bloquant en strict (cohérent avec le plancher 800 et la tolérance 20 %). */
export const THIN_CONTENT_WORDS = 600;

/** Requête longue : bornes en occurrences (KW_DENSITY), exportées pour la finition du moteur. */
export const SEO_KW_LONG = { STEMS: 4, MIN: 2, MAX_FLOOR: 3, MAX_PER_WORDS: 400 } as const;

/** Longueur max d'un intertitre (règle HEADING_LENGTH), exportée pour la finition du moteur. */
export const SEO_HEADING_MAX_CHARS = 70;

export const T = {
  // A. Ciblage
  KW_INTRO_WORDS: 100,
  KW_INTRO_WORDS_WARN: 200,
  KW_HEADINGS_MAX: 2,
  /** Plancher abaissé en 2026.09.3 : le prompt demande de reformuler la requête, la densité brute n'est plus un objectif. */
  KW_DENSITY: { PASS: [0.3, 1.8], WARN: [0.15, 2.5], BLOCK_ABOVE: 3.5 },
  /**
   * Requête longue (≥ 4 racines, 2026.09.5) : la densité n'a plus de sens (0,3 % d'une requête de
   * 6 mots = 5 reprises sur 1 700 mots, lourdes à lire). On compte des occurrences : au moins
   * `MIN`, au plus max(`MAX_FLOOR`, mots / `MAX_PER_WORDS`).
   */
  KW_LONG: SEO_KW_LONG,
  /** Bourrage : répétition de la phrase exacte, par tranche de mots. */
  KW_STUFFING_PASS_PER_WORDS: 150,
  KW_STUFFING_FAIL_PER_WORDS: 100,
  SECONDARY_COVERAGE: { PASS: 0.7, WARN: 0.4 },
  // B. Structure
  H2_WORDS_PER: { PASS: [200, 400], WARN_MAX: 600 },
  H2_REQUIRED_ABOVE_WORDS: 300,
  HEADING_MAX_CHARS: SEO_HEADING_MAX_CHARS,
  INTRO_MIN_WORDS: 40,
  PARAGRAPH_MAX_WORDS: 120,
  PARAGRAPH_OK_RATIO: { PASS: 0.9, WARN: 0.75 },
  SINGLE_SENTENCE_RUN: 4,
  /**
   * Coefficient de variation (écart-type / moyenne) des longueurs de paragraphes : un texte humain
   * dépasse 0,4 sans effort, un texte généré tourne autour de 0,2 (deux paragraphes de trois phrases
   * par section). Calibré sur le corpus du bench (2026-09-06) : médiane 0,27, un tiers sous 0,25 ;
   * seul le vraiment mécanique (< 0,2) échoue, le reste est un avertissement.
   */
  PARAGRAPH_CV: { PASS: 0.35, WARN: 0.2 },
  /** À partir de ce nombre de sections H2 (hors FAQ), « toutes le même nombre de paragraphes » compte. */
  UNIFORM_SECTIONS_MIN: 4,
  LIST_MAX_TEXT_RATIO: 0.4,
  FAQ_MIN_QUESTION_HEADINGS: 3,
  // C. Longueur / métas
  WORD_COUNT_TOLERANCE: { PASS: 0.2, WARN: 0.4 },
  META_TITLE: { PASS: [30, 60], WARN: [25, 70] },
  META_DESC: { PASS: [70, 155], WARN: [50, 170] },
  SLUG_MAX_WORDS: 6,
  SLUG_MAX_CHARS: 75,
  EXCERPT: { PASS: [80, 300] },
  // D. Lisibilité
  SENTENCE_AVG: { PASS: 20, WARN: 25 },
  LONG_SENTENCE_WORDS: 30,
  LONG_SENTENCES_RATIO: { PASS: 0.15, WARN: 0.25 },
  /** Plafond relevé en 2026.09.3 : un français simple est une qualité en B2C local (un bon article sortait à 79). */
  READABILITY: { PASS: [50, 90], WARN: [40, 100] },
  PASSIVE_RATIO: { PASS: 0.08, WARN: 0.15 },
  /**
   * PLAFOND (2026.09.3) : la part de phrases ouvertes ou portées par un connecteur
   * (« en effet », « de plus », « ainsi »…) est la signature la plus reconnaissable
   * du texte généré. L'ancien seuil « ≥ 20 % » exigeait ce que la famille Style combat.
   */
  TRANSITION_RATIO: { PASS_MAX: 0.3, WARN_MAX: 0.45 },
  SAME_START_RUN: 3,
  NOMINAL_STACK_DEPTH: 4,
  // E. Liens
  // Lien de l'appel à l'action compris : 2 liens du brief + l'appel à l'action en général, 5 au plus (2026-10-09).
  INTERNAL_LINKS: { PROFESSIONAL: [2, 5], PROPELSIA: [2, 5] } as Record<string, [number, number]>,
  LINKS_LAST_THIRD_WARN_RATIO: 0.66,
  ANCHOR_WORDS: [2, 8],
  LINK_DENSITY_PER_100: { PASS: 1, WARN: 1.5 },
  // F. Images (une seule image par article : la fiche article n'affiche que la première)
  IMAGE_ALT_CHARS: [5, 125],
  IMAGE_MAX_BYTES: 200 * 1024,
  IMAGE_MAX_WIDTH: 1600,
  // G. Style
  AI_PHRASES: { WARN: 1, FAIL: 3 },
  ANGLICISMS: { WARN: 1, FAIL: 3 },
  EXCLAMATIONS: { PASS: 2, WARN: 5 },
  RHETORICAL_QUESTIONS: { PASS: 3, WARN: 6 },
  NGRAM_N: 4,
  NGRAM_REPEAT_MIN: 3,
  LEXICAL_WINDOW: 200,
  LEXICAL_DIVERSITY: { PASS: 0.55, WARN: 0.45 },
  TRIPLET_PER_WORDS: { PASS: 200, FAIL: 100 },
  CTA_COUNT: { PASS: [1, 2], WARN_MAX: 3 },
  SOURCE_LEAK: { WARN: 1, FAIL: 2 },
  /** Chaînes énumératives (≥ 2 ouvreurs ordinaux dans une même section) : 0 attendu. */
  ENUMERATIVE_CHAINS: { PASS: 0, WARN: 1 },
  /** Marqueurs de méta-discours dans l'introduction. */
  META_INTRO: { WARN: 1, FAIL: 2 },
  /** Autant d'items de liste avant le premier intertitre = plan de raisonnement déversé. */
  REASONING_PREAMBLE_ITEMS: 8,
  // H. Brief
  /** Au-delà : chiffres inventés en série, bloquant en strict. */
  FACT_TRACE_BLOCK_UNTRACED: 3,
  OUTLINE_COVERAGE: { PASS: 0.8, WARN: 0.6 },
  PAA_COVERAGE: { PASS: 0.6, WARN: 0.4 },
  ENTITY_COVERAGE: { PASS: 0.6, WARN: 0.4 },
  QUESTION_MATCH_RATIO: 0.6,
  LENGTH_VS_SERP: { PASS: [0.9, 1.3], WARN: [0.7, 1.6] },
} as const;

export const READING_WORDS_PER_MIN = 220;
